import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ResponseState } from '../State/responses.state';

export const selectResponseState = createFeatureSelector<ResponseState>('responses');

// Basic selectors
export const selectAllResponses = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.responses || []
);

export const selectFilteredResponses = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.filteredResponses || []
);

export const selectFilteredResponsesWithoutParent = createSelector(
  selectFilteredResponses,
  (responses) => responses.filter(response => !response.hasParent)
);

export const selectSearchQuery = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.searchQuery || ''
);

export const selectIsLoading = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.isLoading || false
);

export const selectIsAddingAction = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.isAddingAction || false
);

export const selectError = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.error || null
);

export const selectLastUpdated = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.lastUpdated || null
);

export const selectSelectedResponse = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.selectedResponse || null
);

export const selectSelectedChild = createSelector(
  selectResponseState,
  (state: ResponseState) => state?.selectedChild || null
);

// Computed selectors
export const selectResponsesCount = createSelector(
  selectAllResponses,
  (responses) => responses.length
);

export const selectFilteredResponsesCount = createSelector(
  selectFilteredResponses,
  (responses) => responses.length
);

export const selectUnlinkedResponses = createSelector(
  selectAllResponses,
  (responses) => responses.filter(response => 
    !response.hasOwnProperty('flowIds') || 
    !response.flowIds || 
    response.flowIds.length === 0
  )
);

export const selectLinkedResponses = createSelector(
  selectAllResponses,
  (responses) => responses.filter(response => 
    response.hasOwnProperty('flowIds') && 
    response.flowIds && 
    response.flowIds.length > 0
  )
);

export const selectResponsesWithFlows = createSelector(
  selectAllResponses,
  (responses) => responses.filter(response => 
    response.linkedFlows && response.linkedFlows.length > 0
  )
);

export const selectResponsesWithoutFlows = createSelector(
  selectAllResponses,
  (responses) => responses.filter(response => 
    !response.linkedFlows || response.linkedFlows.length === 0
  )
);

// Search-related selectors
export const selectHasSearchQuery = createSelector(
  selectSearchQuery,
  (query) => query && query.trim().length > 0
);

export const selectIsSearching = createSelector(
  selectHasSearchQuery,
  selectIsLoading,
  (hasQuery, isLoading) => hasQuery && !isLoading
);

// Response by ID selector
export const selectResponseById = (responseId: string) => createSelector(
  selectAllResponses,
  (responses) => responses.find(response => response.id === responseId)
);

// Statistics selectors
export const selectResponseStats = createSelector(
  selectAllResponses,
  selectUnlinkedResponses,
  selectLinkedResponses,
  (all, unlinked, linked) => ({
    total: all.length,
    unlinked: unlinked.length,
    linked: linked.length,
    linkedPercentage: all.length > 0 ? Math.round((linked.length / all.length) * 100) : 0
  })
);
export const selectDisableAddMessage = createSelector(
  selectResponseState,
  (state: ResponseState) => state.disableAddMessage
);